import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchAgents,
  selectAgents,
  updateAgentSettings,
} from '@/redux/agentsSlice';
import { subscriptionPlansApi } from '@/services/api';
import { selectDataRefreshToken } from '@/redux/notificationsSlice';
import type { AppDispatch } from '@/redux/store';
import type { Agent } from '@/types/Agents';
import { Skeleton } from '@/components/ui/skeleton';
import { useIsMobile } from '@/hooks/use-mobile';
import SubscriptionNotification from '@/components/page-components/project/agents/ui/SubscriptionNotification';
import MissingServicesNotification from '@/components/page-components/project/agents/ui/MissingServicesNotification';
import { AppointmentCalendar } from '@/components/page-components/project/agents/calendar/AppointmentCalendar';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import {
  AgentTabNavigation,
  agentTabContentClassName,
} from '@/components/page-components/project/agents/AgentTabNavigation';
import { useToast } from '@/hooks/useToast';
import AgentSettings from '@/components/page-components/project/agents/AgentSettings';
import AgentContacts from '@/components/page-components/project/agents/contacts/AgentContacts';
import { ConversationActionBar } from '@/components/page-components/project/agents/conversations/ConversationActionBar';
import { ConversationTable } from '@/components/page-components/project/agents/conversations/ConversationTable';
import { useConversations } from '@/hooks/useConversations';
import { useConversationListParams } from '@/hooks/useConversationListParams';
import { isBillableConversation } from '@/utils/conversationBilling';
import { AgentBusinessTabPanel } from '@/components/page-components/project/agents/AgentBusinessTabPanel';
import { businessHoursApi } from '@/services/api';
import { useDeferredTabMount } from '@/hooks/useDeferredTabMount';
import { useAgentBusinessData } from '@/hooks/useAgentBusinessData';
import AgentDashboard from '@/components/page-components/project/agents/AgentDashboard';
import {
  formatTimeForSupabase,
  isBusinessHourRangeValid,
} from '@/utils/availability';
import type { Conversation } from '@/types/Conversation';
import { hasCrm, isCrmAgentTab } from '@/utils/crmUtils';
import { resolveAgentTab, type AgentTabValue } from '@/utils/agentTabs';
import {
  businessInfoToFormValue,
  EMPTY_BUSINESS_INFO_FORM,
  formValueToBusinessInfo,
  isValidBusinessEmail,
  isValidBusinessWebsite,
} from '@/utils/businessInfo';
import {
  getPhoneLengthMessage,
  isValidNotificationLocalNumber,
} from '@/utils/phoneUtils';

const LoadingState = () => (
  <div>
    <Skeleton className="h-8 w-full" />
    <Skeleton className="h-32 w-full mt-8" />
    <Skeleton className="h-8 w-3/4 mt-6" />
    <Skeleton className="h-8 w-1/2 mt-6" />
  </div>
);

const AgentPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { agent_id } = useParams<{ agent_id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const isMobile = useIsMobile();

  const [pinBusinessTab, setPinBusinessTab] = useState(false);
  const [isSavingBusiness, setIsSavingBusiness] = useState(false);
  const prevAgentIdRef = useRef<string | null>(null);
  const hasActivatedTabRef = useRef(false);
  const [calendarRefreshToken, setCalendarRefreshToken] = useState(0);
  // Realtime bumps this when a call ends, so an appointment the agent
  // booked or rescheduled mid-call shows up without a reload. Summed with
  // the local token because either source alone should trigger a refetch.
  const realtimeRefreshToken = useSelector(selectDataRefreshToken);
  const { toast } = useToast();

  const agents = useSelector(selectAgents);
  useEffect(() => {
    if (agents.length === 0) {
      void dispatch(fetchAgents());
    }
  }, [dispatch, agents.length]);

  // Reset state when switching between agents
  useEffect(() => {
    if (
      agent_id !== prevAgentIdRef.current &&
      prevAgentIdRef.current !== null
    ) {
      setIsLoading(true);
      void fetchAgentData();
    }

    prevAgentIdRef.current = agent_id || null;
  }, [agent_id]);

  // Find the agent based on the route params
  const agent = agents.find((agent: Agent) => agent.id === Number(agent_id));
  const crmEnabled = agent ? hasCrm(agent) : true;
  const isActive = agent?.subscription?.status === 'active';
  const needsSubscription = Boolean(agent) && !isActive;
  const hasRequestedSubscription = agent?.subscription?.status === 'requested';
  const activeTab: AgentTabValue = resolveAgentTab(
    agent,
    searchParams.get('tab')
  );
  const showBusinessPanel = useDeferredTabMount(activeTab, 'business');
  const {
    businessHours,
    setBusinessHours,
    services,
    setServices,
    availabilityOverrides,
    setAvailabilityOverrides,
    isBusinessHoursLoading,
    isServicesLoading,
    isOverridesLoading,
    refetch: refetchBusinessData,
  } = useAgentBusinessData(agent?.id, {
    includeAvailabilityOverrides: crmEnabled,
  });
  const [businessInfoForm, setBusinessInfoForm] = useState(() => ({
    ...EMPTY_BUSINESS_INFO_FORM,
  }));

  // Conversations hook - must be called unconditionally
  const conversations = agent?.conversations || [];
  const hasNonBillableConversations = conversations.some(
    conversation => !isBillableConversation(conversation)
  );
  const { params: listParams, update: updateListParams } =
    useConversationListParams();
  const { conversations: filteredConversations, requestSort } =
    useConversations(conversations, listParams, {
      contacts: agent?.contacts,
    });

  useEffect(() => {
    if (!agent) return;
    setBusinessInfoForm(
      businessInfoToFormValue(agent.business_info, agent.name)
    );
  }, [agent?.id]);

  // What is currently stored, as the form sees it. Comparing against this is
  // what lets the save button stay disabled until an edit actually changes
  // something -- including after a save, when the stored values move.
  const savedBusinessInfoForm = useMemo(
    () =>
      agent ? businessInfoToFormValue(agent.business_info, agent.name) : null,
    [agent?.id, agent?.business_info, agent?.name]
  );

  const [savedBusinessHours, setSavedBusinessHours] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (isBusinessHoursLoading) return;
    setSavedBusinessHours(current =>
      current === null ? JSON.stringify(businessHours) : current
    );
  }, [isBusinessHoursLoading, businessHours]);

  const isBusinessDirty =
    (savedBusinessInfoForm !== null &&
      JSON.stringify(businessInfoForm) !==
        JSON.stringify(savedBusinessInfoForm)) ||
    (savedBusinessHours !== null &&
      JSON.stringify(businessHours) !== savedBusinessHours);

  useEffect(() => {
    if (showBusinessPanel) setPinBusinessTab(true);
  }, [showBusinessPanel]);

  useEffect(() => {
    setPinBusinessTab(false);
  }, [agent_id]);

  useEffect(() => {
    if (!agent) return;
    const tab = searchParams.get('tab');
    const hasBooking = searchParams.has('booking');
    if (crmEnabled || (!isCrmAgentTab(tab) && !hasBooking)) return;

    const params = new URLSearchParams(searchParams);
    params.set('tab', 'conversations');
    params.delete('booking');
    setSearchParams(params, { replace: true });
  }, [agent, crmEnabled, searchParams, setSearchParams]);

  const fetchAgentData = async () => {
    if (!agent_id) return;

    setIsLoading(true);

    try {
      // Always fetch the full agent data through Redux
      await dispatch(fetchAgents());
    } catch (error) {
      console.error('Error fetching agent data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void dispatch(fetchAgents());

    if (!hasActivatedTabRef.current) {
      hasActivatedTabRef.current = true;
      return;
    }

    if (activeTab === 'business') {
      refetchBusinessData();
    }

    if (activeTab === 'calendar' && crmEnabled) {
      setCalendarRefreshToken(token => token + 1);
    }
  }, [activeTab, crmEnabled, dispatch, refetchBusinessData]);

  const handleTabChange = (value: string) => {
    if (!crmEnabled && isCrmAgentTab(value)) return;
    const params = new URLSearchParams(searchParams);
    params.set('tab', value);
    params.delete('booking');
    setSearchParams(params);
  };

  const bookingParam = searchParams.get('booking');
  const initialBookingId = bookingParam ? Number(bookingParam) : null;
  const validInitialBookingId =
    initialBookingId != null && !Number.isNaN(initialBookingId)
      ? initialBookingId
      : null;

  const handleInitialBookingHandled = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('booking');
    setSearchParams(params);
  };

  const handleResetBusiness = async () => {
    if (!agent_id) return;
    setIsSavingBusiness(true);
    try {
      const refreshed = await businessHoursApi.fetchBusinessHours(
        Number(agent_id)
      );
      setBusinessHours(refreshed);
      setSavedBusinessHours(JSON.stringify(refreshed));
      setBusinessInfoForm(
        businessInfoToFormValue(agent?.business_info, agent?.name)
      );
      toast({
        title: t(
          'agent.settings.reset_success.title.translation',
          'Changes Reset'
        ),
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('agent.business.save_error.title.translation', 'Reset Failed'),
        description:
          error instanceof Error
            ? error.message
            : t('common.unknown_error.translation', 'Unknown error'),
      });
    } finally {
      setIsSavingBusiness(false);
    }
  };

  const handleSaveBusiness = async () => {
    if (!agent_id || !agent) return;
    if (!businessInfoForm.businessName.trim()) {
      toast({
        variant: 'destructive',
        title: t('agent.business.save_error.title.translation', 'Save Failed'),
        description: t(
          'agent.new.guided.validation.business_name.translation',
          'Business name is required.'
        ),
      });
      return;
    }
    const invalidHour = businessHours.find(
      h => !h.is_closed && !isBusinessHourRangeValid(h.start_time, h.end_time)
    );
    if (invalidHour) {
      toast({
        variant: 'destructive',
        title: t('agent.business.save_error.title.translation', 'Save Failed'),
        description: t(
          'agent.business_hours.invalid_range.translation',
          'Closing time must be after opening time for each open day.'
        ),
      });
      return;
    }
    if (!isValidBusinessWebsite(businessInfoForm.website)) {
      toast({
        variant: 'destructive',
        title: t('agent.business.save_error.title.translation', 'Save Failed'),
        description: t(
          'agent.new.guided.validation.business_website.translation',
          'Please enter a valid website.'
        ),
      });
      return;
    }
    if (!isValidBusinessEmail(businessInfoForm.publicEmail)) {
      toast({
        variant: 'destructive',
        title: t('agent.business.save_error.title.translation', 'Save Failed'),
        description: t(
          'agent.new.guided.validation.business_email.translation',
          'Please enter a valid email address.'
        ),
      });
      return;
    }
    if (
      businessInfoForm.publicPhoneLocalNumber &&
      !isValidNotificationLocalNumber(
        businessInfoForm.publicPhoneLocalNumber,
        businessInfoForm.publicPhoneCountryCode
      )
    ) {
      const lengthMessage = getPhoneLengthMessage(
        businessInfoForm.publicPhoneCountryCode
      );
      toast({
        variant: 'destructive',
        title: t('agent.business.save_error.title.translation', 'Save Failed'),
        description: t(lengthMessage.key, {
          defaultValue: lengthMessage.defaultValue,
          ...lengthMessage.values,
        }),
      });
      return;
    }
    setIsSavingBusiness(true);
    try {
      if (businessHours.length > 0) {
        await businessHoursApi.upsertBusinessHours(
          Number(agent_id),
          businessHours.map(h => ({
            day_of_week: h.day_of_week,
            start_time: formatTimeForSupabase(h.start_time),
            end_time: formatTimeForSupabase(h.end_time),
            is_closed: h.is_closed,
          }))
        );
        const refreshed = await businessHoursApi.fetchBusinessHours(
          Number(agent_id)
        );
        setBusinessHours(refreshed);
        setSavedBusinessHours(JSON.stringify(refreshed));
      }

      const businessInfo = formValueToBusinessInfo(businessInfoForm, {
        about: agent.business_info?.about,
      });
      await dispatch(
        updateAgentSettings({
          agentId: Number(agent_id),
          settings: {
            business_info: businessInfo,
          },
        })
      ).unwrap();

      toast({
        title: t(
          'agent.business.save_success.title.translation',
          'Business Settings Saved'
        ),
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('agent.business.save_error.title.translation', 'Save Failed'),
        description:
          error instanceof Error
            ? error.message
            : t('common.unknown_error.translation', 'Unknown error'),
      });
    } finally {
      setIsSavingBusiness(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!agent?.id) return;

    setIsCancelling(true);

    try {
      await subscriptionPlansApi.cancelSubscription(agent.id);
      toast({
        title: t(
          'subscription.cancel_success.title',
          'Subscription Request Cancelled'
        ),
        description: t(
          'subscription.cancel_success.description',
          'Your subscription request has been cancelled successfully.'
        ),
      });
      await fetchAgentData();
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t('common.unknown_error.translation', 'Unknown error');
      toast({
        variant: 'destructive',
        title: t('subscription.cancel_error.title', 'Cancellation Failed'),
        description: t('subscription.cancel_error.description', {
          error: errorMessage,
        }),
      });
    } finally {
      setIsCancelling(false);
    }
  };

  if (!agent) {
    return (
      <div className="flex flex-col gap-4">
        <LoadingState />
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full gap-4 sm:gap-5">
      <div className="flex flex-col w-full items-start">
        <div className="space-y-4 w-full">
          {isLoading ? (
            <LoadingState />
          ) : (
            <>
              {needsSubscription && isMobile && (
                <SubscriptionNotification
                  status={hasRequestedSubscription ? 'requested' : 'required'}
                  fullWidth
                />
              )}
              <div className="flex flex-col md:flex-row md:justify-between gap-4">
                {needsSubscription && !isMobile && (
                  <SubscriptionNotification
                    status={hasRequestedSubscription ? 'requested' : 'required'}
                  />
                )}
              </div>

              {isActive &&
                crmEnabled &&
                !isServicesLoading &&
                services.length === 0 &&
                activeTab !== 'business' && (
                  <MissingServicesNotification
                    onAddServices={() => handleTabChange('business')}
                  />
                )}

              <Tabs
                value={activeTab}
                onValueChange={handleTabChange}
                className="w-full"
              >
                <AgentTabNavigation
                  activeTab={activeTab}
                  placement="top"
                  showCalendarTab={crmEnabled}
                />

                {crmEnabled && (
                  <TabsContent
                    value="calendar"
                    className={agentTabContentClassName}
                  >
                    {agent?.id && (
                      <AppointmentCalendar
                        agentId={agent.id}
                        services={services}
                        contacts={agent.contacts || []}
                        initialBookingId={validInitialBookingId}
                        onInitialBookingHandled={handleInitialBookingHandled}
                        readOnly={needsSubscription}
                        externalBusinessHours={businessHours}
                        externalAvailabilityOverrides={availabilityOverrides}
                        refreshToken={
                          calendarRefreshToken + realtimeRefreshToken
                        }
                        onContactCreated={async () => {
                          await fetchAgentData();
                        }}
                      />
                    )}
                  </TabsContent>
                )}

                <TabsContent
                  value="conversations"
                  className={agentTabContentClassName}
                >
                  {agent?.id && (
                    <div className="flex flex-col w-full gap-4">
                      <ConversationActionBar
                        params={listParams}
                        onChange={updateListParams}
                        showBillingFilter={hasNonBillableConversations}
                        resultCount={filteredConversations.length}
                        totalCount={conversations.length}
                      />
                      <ConversationTable
                        conversations={filteredConversations}
                        page={listParams.page}
                        onPageChange={page => updateListParams({ page })}
                        subscription={agent.subscription}
                        requestSort={(key: string, subKey?: string) =>
                          requestSort(
                            key as keyof Conversation,
                            subKey as 'length' | 'count'
                          )
                        }
                        contacts={agent.contacts}
                        agentId={agent.id}
                      />
                    </div>
                  )}
                </TabsContent>

                <TabsContent
                  value="contacts"
                  className={agentTabContentClassName}
                >
                  {agent?.id && <AgentContacts />}
                </TabsContent>

                <TabsContent
                  value="business"
                  forceMount={pinBusinessTab ? true : undefined}
                  className={agentTabContentClassName}
                >
                  {agent?.id && (
                    <AgentBusinessTabPanel
                      agentId={agent.id}
                      ready={showBusinessPanel}
                      businessHours={businessHours}
                      isBusinessHoursLoading={isBusinessHoursLoading}
                      services={services}
                      isServicesLoading={isServicesLoading}
                      onServicesChange={setServices}
                      availabilityOverrides={availabilityOverrides}
                      isOverridesLoading={isOverridesLoading}
                      onOverridesChange={setAvailabilityOverrides}
                      showAvailabilityOverrides={crmEnabled}
                      crmEnabled={crmEnabled}
                      businessInfoForm={businessInfoForm}
                      onBusinessInfoChange={setBusinessInfoForm}
                      isSavingBusiness={isSavingBusiness}
                      isBusinessDirty={isBusinessDirty}
                      onBusinessHoursChange={setBusinessHours}
                      onReset={() => {
                        void handleResetBusiness();
                      }}
                      onSave={() => {
                        void handleSaveBusiness();
                      }}
                    />
                  )}
                </TabsContent>

                <TabsContent value="usage" className={agentTabContentClassName}>
                  {agent && (
                    <div className="flex flex-col w-full max-w-4xl mx-auto">
                      <AgentDashboard agent={agent} />
                    </div>
                  )}
                </TabsContent>

                <TabsContent
                  value="settings"
                  className={agentTabContentClassName}
                >
                  {agent?.id && (
                    <div className="flex flex-col w-full max-w-4xl mx-auto max-lg:px-0 lg:px-4">
                      <AgentSettings
                        agentId={agent.id}
                        showSubscriptionOptions={needsSubscription}
                        onSubscriptionRequested={async () => {
                          await fetchAgentData();
                        }}
                        onCancelSubscription={handleCancelSubscription}
                        isCancellingSubscription={isCancelling}
                      />
                    </div>
                  )}
                </TabsContent>

                <AgentTabNavigation
                  activeTab={activeTab}
                  placement="bottom"
                  showCalendarTab={crmEnabled}
                />
              </Tabs>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentPage;
